import React, { useRef, useState, useMemo, useCallback } from "react";
import ApexCharts from "react-apexcharts";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  Button,
  Chip,
  Alert,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import Loading from "../../../components/spinners/Loading";
import Iconify from "../../../components/iconify";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract the raw value from an answer cell (handles both legacy and new format). */
const getCellValue = (cell) => {
  if (cell === null || cell === undefined) return null;
  if (typeof cell === "object" && cell !== null) {
    // New format: { questionId, value } or { questionId, values }
    return cell.values || cell.value;
  }
  // Legacy: primitive
  return cell;
};

const SMILEY_MAPPING = {
  1: "\uD83D\uDE21",
  2: "\uD83D\uDE20",
  3: "\uD83D\uDE10",
  4: "\uD83D\uDE42",
  5: "\uD83D\uDE03",
};

const OPINION_MAPPING = {
  1: "Stimme \u00fcberhaupt nicht zu",
  2: "Stimme nicht zu",
  3: "Neutral",
  4: "Stimme zu",
  5: "Stimme voll und ganz zu",
};

const aggregateAnswers = (answers, range, mapping) => {
  const counts = range.reduce((acc, key) => {
    const label = mapping ? mapping[key] : key;
    acc[label] = 0;
    return acc;
  }, {});

  answers.forEach((answer) => {
    const label = mapping ? mapping[answer] : answer;
    if (label !== undefined && counts[label] !== undefined) {
      counts[label]++;
    }
  });

  return counts;
};

/** Aggregate choice-type answers. Each answer is a single value or array of values. */
const aggregateChoiceAnswers = (answers, options) => {
  const counts = {};
  (options || []).forEach((opt) => {
    counts[opt.label] = 0;
  });

  answers.forEach((answer) => {
    const vals = Array.isArray(answer) ? answer : answer != null ? [answer] : [];
    vals.forEach((v) => {
      const opt = (options || []).find((o) => o.value === v || o.label === v);
      if (opt && counts[opt.label] !== undefined) {
        counts[opt.label]++;
      }
    });
  });

  return counts;
};

const getAnswerRange = (type) => {
  switch (type) {
    case "points":
      return Array.from({ length: 10 }, (_, i) => i + 1);
    case "smiley":
      return { 1: "\uD83D\uDE21", 2: "\uD83D\uDE20", 3: "\uD83D\uDE10", 4: "\uD83D\uDE42", 5: "\uD83D\uDE03" };
    case "opinion":
      return {
        1: "Stimme \u00fcberhaupt nicht zu",
        2: "Stimme nicht zu",
        3: "Neutral",
        4: "Stimme zu",
        5: "Stimme voll und ganz zu",
      };
    default:
      return [];
  }
};

/** Get the clickable filter chip options for a question type. */
const getFilterOptions = (question) => {
  switch (question.type) {
    case "points":
      return Array.from({ length: 10 }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1),
      }));
    case "smiley":
      return [1, 2, 3, 4, 5].map((n) => ({
        value: String(n),
        label: SMILEY_MAPPING[n],
      }));
    case "opinion":
      return [1, 2, 3, 4, 5].map((n) => ({
        value: String(n),
        label: OPINION_MAPPING[n],
      }));
    case "select":
    case "singleChoice":
    case "multipleChoice":
      return (question.options || []).map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    default:
      return [];
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SurveyEvaluationView = ({ surveyQuestions, surveyAnswers }) => {
  const contentRef = useRef(null);
  const [printState, setPrintState] = useState({
    loading: false,
    style: { marginTop: 25 },
    noPrintStyle: {},
  });
  const [activeFilters, setActiveFilters] = useState({});
  const printContent = useReactToPrint({ contentRef });

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  // --- Filter logic ---

  const toggleFilter = useCallback((questionIndex, value) => {
    setActiveFilters((prev) => {
      const current = prev[questionIndex] || [];
      const strValue = String(value);
      const isActive = current.includes(strValue);

      const next = isActive
        ? current.filter((v) => v !== strValue)
        : [...current, strValue];

      if (next.length === 0) {
        const { [questionIndex]: _, ...rest } = prev;
        return rest;
      }

      return { ...prev, [questionIndex]: next };
    });
  }, []);

  const clearFilters = useCallback(() => setActiveFilters({}), []);

  const filteredAnswers = useMemo(() => {
    const filterEntries = Object.entries(activeFilters);
    if (filterEntries.length === 0) return surveyAnswers;

    return surveyAnswers.filter((submission) =>
      filterEntries.every(([qIdx, values]) => {
        const idx = parseInt(qIdx, 10);
        const raw = getCellValue(submission.answers[idx]);
        if (raw === null || raw === undefined) return false;

        if (Array.isArray(raw)) {
          return values.some((v) => raw.map(String).includes(v));
        }
        return values.includes(String(raw));
      })
    );
  }, [surveyAnswers, activeFilters]);

  // --- Print ---

  const handlePrint = () => {
    setPrintState({
      loading: true,
      style: { width: "100%", maxWidth: "800px" },
      noPrintStyle: { display: "none !important" },
    });
    setTimeout(() => {
      console.log("print now!");
      printContent();
      setPrintState({
        loading: false,
        style: { marginTop: 25 },
        noPrintStyle: {},
      });
    }, 1500);
  };

  // --- Aggregate data (uses filteredAnswers) ---

  const aggregatedData = surveyQuestions.map((question, index) => {
    const answers = filteredAnswers.map((response) => {
      const raw = getCellValue(response.answers[index]);
      return typeof raw === "string" && !isNaN(Number(raw))
        ? Number(raw)
        : raw;
    });

    if (question.type === "smiley") {
      return {
        question,
        counts: aggregateAnswers(answers, [1, 2, 3, 4, 5], SMILEY_MAPPING),
      };
    }

    if (question.type === "opinion") {
      return {
        question,
        counts: aggregateAnswers(answers, [1, 2, 3, 4, 5], OPINION_MAPPING),
      };
    }

    if (question.type === "points") {
      return {
        question,
        counts: aggregateAnswers(
          answers,
          Array.from({ length: 10 }, (_, i) => i + 1)
        ),
      };
    }

    if (question.type === "openQuestion") {
      return { question, responses: answers.filter((ans) => ans) };
    }

    // Choice types: select, singleChoice, multipleChoice
    if (
      question.type === "select" ||
      question.type === "singleChoice" ||
      question.type === "multipleChoice"
    ) {
      return {
        question,
        counts: aggregateChoiceAnswers(answers, question.options),
        isChoice: true,
      };
    }

    // Unknown type – render as open responses to avoid crash
    return { question, responses: answers.filter((ans) => ans) };
  });

  // Generate ApexCharts options for each quantitative question
  const generateChartOptions = (counts, questionText, type, isChoice) => {
    let categories;
    if (isChoice) {
      categories = Object.keys(counts);
    } else if (Array.isArray(getAnswerRange(type))) {
      categories = getAnswerRange(type);
    } else {
      categories = Object.values(getAnswerRange(type));
    }

    return {
      series: [
        {
          name: "Anzahl der Antworten",
          data: categories.map((label) => counts[label] || 0),
        },
      ],
      options: {
        chart: {
          type: "bar",
          toolbar: {
            show: true,
            tools: { download: true },
          },
        },
        xaxis: {
          categories,
          title: {
            text: questionText,
            offsetY: type === "smiley" ? -20 : 0,
          },
          labels: {
            style: {
              fontSize: type === "smiley" ? "24px" : "14px",
            },
          },
        },
        yaxis: {
          title: { text: "Anzahl der Antworten" },
          labels: { formatter: (value) => Math.round(value) },
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: (value) => `${value} Antwort${value === 1 ? "" : "en"}`,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => Math.round(value),
        },
      },
    };
  };

  // --- Render ---

  return (
    <Box>
      <Button
        size="large"
        color="secondary"
        variant="contained"
        onClick={() => handlePrint()}
      >
        Komplette Auswertung exportieren
      </Button>

      {/* Filter status bar */}
      {hasActiveFilters && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
          icon={<Iconify icon="mdi:filter" width={22} />}
          action={
            <Button color="inherit" size="small" onClick={clearFilters}>
              Alle Filter zur\u00fccksetzen
            </Button>
          }
        >
          <strong>Filter aktiv:</strong> {filteredAnswers.length} von{" "}
          {surveyAnswers.length} Antworten
        </Alert>
      )}

      {printState.loading && <Loading backdrop={true} />}
      <div ref={contentRef}>
        <div style={{ ...printState.style }}>
          <Typography variant="h4" gutterBottom align="left">
            Anzahl der Antworten:{" "}
            {hasActiveFilters
              ? `${filteredAnswers.length} / ${surveyAnswers.length}`
              : surveyAnswers?.length}
          </Typography>
          {aggregatedData.map((data, index) => {
            if (!data) return null;
            const { question, counts, responses, isChoice } = data;
            const filterOptions = getFilterOptions(question);
            const activeValues = activeFilters[index] || [];
            const isFilterSource = activeValues.length > 0;

            return (
              <Card
                key={index}
                sx={{
                  my: 3,
                  ...(isFilterSource && {
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                  }),
                }}
              >
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {question.question}
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  {/* Filter chips */}
                  {filterOptions.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Iconify
                        icon="mdi:filter-outline"
                        width={18}
                        sx={{ color: "text.secondary", mr: 0.5, flexShrink: 0 }}
                      />
                      {filterOptions.map((opt) => {
                        const isActive = activeValues.includes(opt.value);
                        return (
                          <Chip
                            key={opt.value}
                            label={opt.label}
                            size="small"
                            variant={isActive ? "filled" : "outlined"}
                            color={isActive ? "primary" : "default"}
                            onClick={() => toggleFilter(index, opt.value)}
                            sx={{ cursor: "pointer" }}
                          />
                        );
                      })}
                    </Box>
                  )}

                  {/* Bar Chart for quantitative + choice data */}
                  {counts && (
                    <ApexCharts
                      options={
                        generateChartOptions(
                          counts,
                          question.question,
                          question.type,
                          isChoice
                        ).options
                      }
                      series={
                        generateChartOptions(
                          counts,
                          question.question,
                          question.type,
                          isChoice
                        ).series
                      }
                      type="bar"
                      height={350}
                    />
                  )}

                  {/* Open Question / unknown type Responses */}
                  {responses && (
                    <Box>
                      <List>
                        {responses.map((response, idx) => (
                          <ListItem
                            key={idx}
                            sx={{ borderBottom: "1px solid #ddd" }}
                          >
                            {typeof response === "object"
                              ? JSON.stringify(response)
                              : response}
                          </ListItem>
                        ))}
                      </List>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        style={printState.noPrintStyle}
                        onClick={() =>
                          exportResponses(responses, question.question)
                        }
                      >
                        Antworten exportieren
                      </Button>
                    </Box>
                  )}
                </CardContent>
                <div
                  style={{
                    marginTop: "1rem",
                    display: "block",
                    pageBreakBefore: "always",
                  }}
                />
              </Card>
            );
          })}
        </div>
      </div>
    </Box>
  );
};

// Helper Function: Export Open Responses as a Text File
const exportResponses = (responses, questionTitle) => {
  const blob = new Blob([responses.join("\n")], { type: "text/plain" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${questionTitle}-antworten.txt`;
  link.click();
};

export default SurveyEvaluationView;
